/**
 * The geocode fields of a location are ONE unit, valid only for the text it answered.
 *
 * The agent stamps every geocode answer with `geocodedFor` = `geocodeStamp(location)`
 * (mirror of geocode_stamp in agent-backend/demo/geocode_state.py; the output is identical).
 * The reducer therefore never merges these fields one by one:
 *   - an answer stamped for text the stored location no longer has is dropped whole;
 *   - an answer stamped for the stored text replaces the stored unit verbatim;
 *   - a text change with no new answer resets the unit to pending.
 * Locations stored without `geocodedFor` (legacy) keep the generic field-by-field merge.
 */

type Loc = Record<string, any>;

export const GEOCODE_UNIT_FIELDS = [
  'latitude',
  'longitude',
  'geocodingStatus',
  'geocodingError',
  'geocodedAddress',
  'geocodeConfidence',
  'geocodeProvider',
  'geocodedFor',
  'isApproximate',
  'locationPrecision',
  'approximateOf',
  'approximateResolvedTo',
  'approximateKmFromAnchor',
  'jurisdiction',
] as const;

const lowered = (value: unknown): string => (typeof value === 'string' ? value.trim().toLowerCase() : '');

export const geocodeStamp = (loc: Loc): string => `${lowered(loc?.address)}|${lowered(loc?.name)}`;

const hasStamp = (loc: Loc): boolean => typeof loc?.geocodedFor === 'string';

const hasText = (loc: Loc): boolean => lowered(loc?.address) !== '' || lowered(loc?.name) !== '';

/** True when `incoming` carries an answer for text other than the stored location's. */
export function isStaleGeocodeUnit(existing: Loc, incoming: Loc): boolean {
  if (!hasStamp(incoming)) return false;
  const answered = hasText(existing) ? geocodeStamp(existing) : geocodeStamp(incoming);
  return incoming.geocodedFor !== answered;
}

/** Apply the unit rules to `merged` (the generic merge of existing + incoming), in place. */
export function applyGeocodeUnit(existing: Loc, incoming: Loc, merged: Loc): void {
  if (hasStamp(incoming)) {
    for (const field of GEOCODE_UNIT_FIELDS) {
      if (incoming[field] === undefined) delete merged[field];
      else merged[field] = incoming[field];
    }
    return;
  }
  if (hasStamp(existing) && existing.geocodedFor !== geocodeStamp(merged)) {
    for (const field of GEOCODE_UNIT_FIELDS) delete merged[field];
    merged.geocodingStatus = 'pending';
  }
}
